import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { logementAPI } from '../../service/api';
import { FiSearch, FiMapPin, FiHome, FiMap } from 'react-icons/fi';
import { resolveUploadUrl } from '../../config/config';
import PageMeta from '../../components/PageMeta';
import useAutoRefresh from '../../hooks/useAutoRefresh';

const types = ['', 'studio', 'appartement', 'maison', 'chambre'];
const typeLabels = { studio: 'Studio', appartement: 'Appartement', maison: 'Maison', chambre: 'Chambre' };

export default function LogementList() {
  const [logements, setLogements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: '', prixMax: '', search: '' });
  const [pagination, setPagination] = useState({ page: 1, total: 0 });
  const [imgErrors, setImgErrors] = useState({});

  const fetchLogements = async (skipLoading = false) => {
    if (!skipLoading) setLoading(true);
    try {
      const params = { page: pagination.page, limit: 12 };
      if (filters.type) params.type = filters.type;
      if (filters.prixMax) params.prixMax = filters.prixMax;
      if (filters.search) params.search = filters.search;
      const res = await logementAPI.getAll(params);
      setLogements(res.data.properties);
      setPagination({ ...pagination, total: res.data.total });
    } catch (err) { console.error(err); }
    finally { if (!skipLoading) setLoading(false); }
  };

  useEffect(() => { fetchLogements(); }, [pagination.page]);
  useAutoRefresh(() => fetchLogements(true), 30000);

  const handleSearch = (e) => { e.preventDefault(); setPagination({ ...pagination, page: 1 }); fetchLogements(); };

  return (
    <div className="page-container py-8">
      <PageMeta title="Logements" description="Trouvez un logement vérifié près de l'UNIKOL à Kolwezi" />
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-bold tracking-tight" style={{ color: '#0A192F' }}>Logements</h1>
          <p className="text-sm mt-1" style={{ color: '#7A828A' }}>Trouvez le logement qui vous correspond</p>
        </div>
        <Link to="/logements/carte" className="btn-outline-root flex items-center gap-2">
          <FiMap /> Vue carte
        </Link>
      </div>

      <form onSubmit={handleSearch} className="card-root p-5 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#7A828A' }} />
            <input className="input-root pl-10" placeholder="Rechercher par titre, description, adresse..."
              value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
          </div>
          <select className="input-root md:w-44" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
            <option value="">Tous les types</option>
            {types.filter(Boolean).map((t) => (
              <option key={t} value={t}>{typeLabels[t] || t}</option>
            ))}
          </select>
          <input className="input-root md:w-36" type="number" placeholder="PRIX MAX" value={filters.prixMax}
            onChange={(e) => setFilters({ ...filters, prixMax: e.target.value })} />
          <button type="submit" className="btn-primary-root">Filtrer</button>
        </div>
      </form>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2" style={{ borderColor: '#C59B6C', borderTopColor: 'transparent' }} />
        </div>
      ) : logements.length === 0 ? (
        <div className="text-center py-20">
          <FiHome className="w-16 h-16 mx-auto mb-4" style={{ color: '#E5E5E0' }} />
          <p style={{ color: '#7A828A' }}>Aucun logement trouvé</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {logements.map((logement) => (
            <Link key={logement.id} to={`/logements/${logement.id}`} className="card-root overflow-hidden hover:shadow-md transition-shadow">
              <div className="h-48 flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#E5E5E0' }}>
                {logement.images && logement.images.length > 0 && !imgErrors[logement.id] ? (
                  <img src={resolveUploadUrl(logement.images[0])} alt={logement.titre} className="w-full h-full object-cover"
                    onError={() => setImgErrors(prev => ({ ...prev, [logement.id]: true }))} />
                ) : (
                  <FiHome className="w-12 h-12" style={{ color: '#88929B' }} />
                )}
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-bold tracking-widest uppercase px-3 py-1 border-2"
                    style={{ color: '#0A192F', borderColor: '#0A192F' }}>{typeLabels[logement.type] || logement.type}</span>
                  <span className="font-bold text-lg" style={{ color: '#C59B6C' }}>{Number(logement.prix).toLocaleString()} $</span>
                </div>
                <h3 className="font-serif font-bold text-base mb-1" style={{ color: '#0A192F' }}>{logement.titre}</h3>
                <p className="text-xs flex items-center gap-1" style={{ color: '#7A828A' }}>
                  <FiMapPin className="w-3 h-3" /> {logement.adresse}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}

      {pagination.total > 12 && (
        <div className="flex justify-center items-center gap-4 mt-8">
          <button className="btn-outline-root btn-sm" disabled={pagination.page <= 1}
            onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}>
            Précédent
          </button>
          <span className="text-sm" style={{ color: '#7A828A' }}>Page {pagination.page} / {Math.ceil(pagination.total / 12)}</span>
          <button className="btn-outline-root btn-sm" disabled={pagination.page >= Math.ceil(pagination.total / 12)}
            onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}>
            Suivant
          </button>
        </div>
      )}
    </div>
  );
}
